// 3rd-party modules
import { isAndroid, isIOS, isMobile } from 'react-device-detect';
import { useEffect, useState } from 'react';
import { Tooltip, message } from 'antd';

// project modules
import Button from '../../shared/button';
import Popup from '../../shared/popup/popup';
import Loader from '../../shared/loader';
import Select from '../../shared/inputs/select';
import { apiCall } from '../../../helpers/apiHelper';

// apis
import * as AccountKiboshDeviceApi from '../../../apis/accountKiboshDeviceApi';
import * as CommonValueApi from '../../../apis/commonValueApi';
import * as DashApi from '../../../apis/dashApi';

// models
import { Account } from '../../../models/account';
import { AccountKiboshDevice } from '../../../models/accountKiboshDevice';
import { AccountKiboshVpnDevice } from '../../../models/accountKiboshVpnDevice';
import { DataSourceRequest } from '../../../models/dataSourceRequest';
import { AccountDevice } from '../../../models/accountDevice';
import { ApiResponse } from '../../../models/response';
import { CommonValue } from '../../../models/commonValue';
import { AccountVpnConfigUrl } from '../../../models/accountVpnConfigUrl';

type Props = {
  open: boolean;
  account: Account;
  accountKiboshDevice: AccountKiboshDevice;
  accountKiboshVpnDevice: AccountKiboshVpnDevice;
  vpnAssignedDevicesIds?: string[];
  modalTitle?: string;
  onClose?: () => void;
  onSave?: () => void;
};

export default function AccountVpnDevicePickerModal({ open, account, accountKiboshDevice, accountKiboshVpnDevice, modalTitle = "", vpnAssignedDevicesIds = [], onClose, onSave }: Props) {
  const [accountDevices, setAccountDevices] = useState<AccountDevice[]>([]);
  const [currentAccountDeviceId, setCurrentAccountDeviceId] = useState<string>("");
  const [currentAccountKiboshVpnDevice, setCurrentAccountKiboshVpnDevice] = useState<AccountKiboshVpnDevice>(accountKiboshVpnDevice);
  const [vpnAssignedDeviceId, setVpnAssignedDeviceId] = useState<any>(accountKiboshVpnDevice.associatedRouterClientId);
  const [visibleAccountDevices, setVisibleAccountDevices] = useState<AccountDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState<boolean>(false);
  const [isDeviceAssignedRecently, setIsDeviceAssignedRecently] = useState<boolean>(false);

  const abortController = new AbortController();

  useEffect(() => {
    if (open) {
      if (currentAccountKiboshVpnDevice.kiboshDeviceReference?.startsWith("new-")) {
        onVpnCreation();
      }
      else {
        setReload(true);
      }
    }
  }, [open])

  useEffect(() => {
    if (account.accountId) {
      getAccountDevicesAsync()
    }
  }, [account])

  useEffect(() => {
    if (reload) {
      getAccountDevicesAsync();
      setReload(false);
    }
  }, [reload]);

  useEffect(() => {
    setVisibleAccountDevices(accountDevices.filter(x => !vpnAssignedDevicesIds.includes(x.id!)));
  }, [vpnAssignedDevicesIds])

  const getDeviceTypes = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(CommonValueApi.getDeviceTypes(abortSignal));

    return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
  }

  const onCancel = () => {
    abortController.abort();

    if(onClose) onClose();
  };

  const onVpnCreation = async () => {
    let response: ApiResponse;

    setLoading(true);

    let formData = {...currentAccountKiboshVpnDevice};
    const deviceTypes = await getDeviceTypes(abortController.signal);

    const deviceVpnType = deviceTypes.filter(x => x.value === "vpn");

    if (deviceVpnType.length) {
      formData.kiboshDeviceTypeId = deviceVpnType[0].commonValueId;
    }

    formData.kiboshDeviceReference = accountKiboshDevice.kiboshDeviceReference;

    setCurrentAccountKiboshVpnDevice(formData)

    response = await apiCall(AccountKiboshDeviceApi.insertAccountKiboshVpnDevice(formData, abortController.signal));

    if (response.success) {
      setCurrentAccountKiboshVpnDevice({...currentAccountKiboshVpnDevice, kiboshDeviceReference: response.data?.value?.notes })
      message.success("VPN created successfully.");
      setReload(true);

      if (onSave) onSave();

    } else
      message.error(response.error?.value);

    setLoading(false);
  };

  const onVpnTypeSetClick = async (vpnType: string) => {
    let response: ApiResponse;

    setLoading(true);
    let formData = {...currentAccountKiboshVpnDevice};
    formData.notes = vpnType;

    response = await apiCall(DashApi.insertVpnConfig(formData, accountKiboshDevice.kiboshDeviceReference,abortController.signal));

    if (response.success) {
      message.success("VPN Type set successfully.");

      setCurrentAccountKiboshVpnDevice({...currentAccountKiboshVpnDevice, vpnType: vpnType })
      setIsDeviceAssignedRecently(true);
      if (onSave) onSave();

    } else
      message.error(response.error?.value);

    setLoading(false);
  };

  const getAccountDevicesAsync = async () => {
    if (accountKiboshDevice?.kiboshDeviceTypeId == 1) {
      const data = await getAccountDevices({}, abortController.signal);

      // checking if associated device is not a part of incomming data.
      if (currentAccountKiboshVpnDevice?.associatedRouterClientId && !data.filter(x => x.id === currentAccountKiboshVpnDevice?.associatedRouterClientId)) {
        // adding temp data to array of incoming devices to be able to show associated device name in dropdown
        const tempDevice = new AccountDevice();
        tempDevice.id = currentAccountKiboshVpnDevice?.associatedRouterClientId;
        tempDevice.name = currentAccountKiboshVpnDevice?.name;

        data.push(tempDevice);
      }
      setAccountDevices(data);

      if (data.length) {
        // to show only unassigned devices and this vpn device in dropdown
        const otherVpnsAssignedDevicesIds = vpnAssignedDevicesIds.filter(x => x !== vpnAssignedDeviceId)
        setVisibleAccountDevices(data.filter(x => !otherVpnsAssignedDevicesIds.includes(x.id!)));
      }
      else {
        setVisibleAccountDevices([]);
      }
    }
    else {
      const tempDevice = new AccountDevice();
      tempDevice.id = currentAccountKiboshVpnDevice.kiboshDeviceReference;
      tempDevice.name = currentAccountKiboshVpnDevice.kiboshDeviceReference;

      setVisibleAccountDevices([tempDevice]);
      setCurrentAccountDeviceId(currentAccountKiboshVpnDevice.kiboshDeviceReference || "");
      setVpnAssignedDeviceId(currentAccountKiboshVpnDevice.kiboshDeviceReference || "");
      setIsDeviceAssignedRecently(false);
    }
  }

  const getAccountDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoading(true);
    const response = await apiCall(DashApi.getClientDevices(accountKiboshDevice?.kiboshDeviceReference!, abortSignal));
    setLoading(false);

    if (response.success) {
      return AccountDevice.toArrayOfClass(response.data?.value || []).map((x: any) => {
        return {
          ...x,
          sorter: x.groupId ? Number(x.groupId) : Infinity
        }
      }).sort((a: any, b: any) => a.sorter - b.sorter);

    }
    else {
      return [];
    }
  };

  const getVpnConfigUrl = async () => {
    setLoading(true);
    const response = await apiCall(DashApi.getVpnConfigUrl(
      currentAccountKiboshVpnDevice.kiboshDeviceReference!,
      abortController.signal
    ));
    setLoading(false);

    return response.success ? AccountVpnConfigUrl.toClass(response.data?.value) : null;
  }

  const onAddNewDeviceToGroup = async () => {
    setLoading(true);
    let response: ApiResponse = await apiCall(DashApi.associateClientDeviceToVpn(accountKiboshDevice?.kiboshDeviceReference!, currentAccountDeviceId, currentAccountKiboshVpnDevice.kiboshDeviceReference!, abortController.signal));

    if (response.success) {
      message.success(`Device added successfully.`);
      setCurrentAccountDeviceId("");
      setVpnAssignedDeviceId(currentAccountDeviceId);
      setReload(true);
      if(onSave) onSave();
    } else
      message.error(response.error?.value);

    setLoading(false);
  }

  const onInstallClick = async (event?: any) => {
    if (event) {
      event.stopPropagation();
    }

    var vpnConfigObj = await getVpnConfigUrl();

    if (vpnConfigObj) {
      const iosScheme = vpnConfigObj.url!
      const androidScheme = vpnConfigObj.url!

      if(isMobile)
      {
        if (isIOS) {
          // iOS
          window.location.href = iosScheme;
        } else if (isAndroid) {
          // Android
          window.location.href = androidScheme;
        } else {
          message.error('This action requires the app to be installed.');
        }
      } else {
        message.error("You need to install this application on mobile")
      }

    }
  };

  const onOpenVpnConfigClick = async (event?: any) => {
    if (event) {
      event.stopPropagation();
    }

    var vpnConfigObj = await getVpnConfigUrl();

    if (vpnConfigObj) {
      window.location.href=vpnConfigObj.downloadLink!
    }
  };



  const onDeleteClick = async () => {
    setLoading(true);
    Promise.all([apiCall(DashApi.dissociateClientDeviceFromVpn(accountKiboshDevice?.kiboshDeviceReference!, vpnAssignedDeviceId, abortController.signal)), apiCall(DashApi.deleteVpnConfig(currentAccountKiboshVpnDevice.kiboshDeviceReference!, accountKiboshDevice.kiboshDeviceReference, abortController.signal))]).then(
      (results: any) => {
        if (results[0].success && results[1].success) {
          message.success("VPN removed from device successfully.");
          if (onSave) onSave();
          onCancel();
        }

        setLoading(false);
      }
    );
  };


  const getDeviceOption = (deviceItem:any) => {
    let isVpn = ( deviceItem["is_vpn"] == true )
    let dv =  deviceItem.id! 
    let dt  =  deviceItem.name!
    dt = dt == null ? "" + deviceItem.id! : dt
    dt = isVpn ? dt  + " (VPN)" : dt
    dt = (!isVpn) && deviceItem.ip ? dt + " (" + deviceItem.ip.replace("disconnected", "offline") + ")" : dt
    return { text: dt, value: "" + dv }
  };

  const getDeviceTitle = (deviceId:any) => {
    let dev = accountDevices.find(x => x.id == deviceId)
    return dev == null ? "" : getDeviceOption(dev).text
  };


  return (
    <Popup
      adaptive
      title={ modalTitle || "VPN Device"}
      onCancel={onCancel}
      onClose={onCancel}
      fixSize="large"
      noCommandbar={true}
    >
      {loading && <Loader />}
      <view
        data-scroll=""
        data-space="30"
        data-gap="30">
        <group data-gap="10" data-direction="column">
          <text data-weight="700" data-text-size="xx-large" data-wrap="wrap" data-color="main-dark">VPN Device</text>
          <text data-wrap="wrap" data-length="300" data-line="1.5" data-light="">Here you can manage all your profile devices.</text>
        </group>
        <group data-width="auto" data-position="left" data-align="center" data-gap="10">
          <group data-width="auto" data-position="left" data-align="center" data-gap="10" data-space="10" data-radius="5" data-elevation="1" data-backdrop="">
            { !vpnAssignedDeviceId && <>
            <Select
              autoComplete=""
              label="Device"
              labelPosition="left"
              dataLength="320"
              value={vpnAssignedDeviceId || currentAccountDeviceId}
              onChange={(e: any) => setCurrentAccountDeviceId(e)}
              loading={loading}
              allowSearch={true}
              options={
                visibleAccountDevices?.map((item) => { return getDeviceOption(item); }) || []
              }
            />
              <group data-width="auto">
                <Button
                  secondary
                  disabled={!currentAccountDeviceId || !visibleAccountDevices.length ? true : undefined}
                  icon="add"
                  text="Assign Device to VPN"
                  onClick={onAddNewDeviceToGroup}
                />
              </group>
              </>
            }
            { vpnAssignedDeviceId &&
              <text>VPN Assigned To: { getDeviceTitle( (vpnAssignedDeviceId ? vpnAssignedDeviceId : currentAccountDeviceId)) }</text>
            }
          </group>
          {
            !vpnAssignedDeviceId &&
            <separator data-vertical=""></separator>
          }
          <Tooltip title="Refresh" arrow={false}>
            <group data-width="auto">
              <Button
                primary
                large
                icon="refresh"
                onClick={() => setReload(true)}
              />
            </group>
          </Tooltip>
        </group>
        { (vpnAssignedDeviceId && !currentAccountKiboshVpnDevice.vpnType) &&
          <group data-align="center" data-gap="10" data-direction="column">
            <text>What type of device?</text>
            <Button large rounded outline text="Apple / IOS" onClick={() => onVpnTypeSetClick("ipsec")}/>
            <Button large rounded outline text="Android" onClick={() => onVpnTypeSetClick("openvpn")}/>
          </group>
        }
        { (isDeviceAssignedRecently && vpnAssignedDeviceId && currentAccountKiboshVpnDevice.vpnType) &&
          <group data-align="center" data-gap="10" data-direction="column">
            <Button large rounded outline text="Download Your VPN" onClick={onInstallClick}/>
            { account?.accountId == 10010 &&
              <Button large rounded outline text="Download OpenVPN Config File" onClick={onOpenVpnConfigClick}/>
            }
          </group>
        }
        { vpnAssignedDeviceId &&
        <group data-align="center" data-gap="10" data-direction="column">
          { (!isDeviceAssignedRecently && vpnAssignedDeviceId && currentAccountKiboshVpnDevice.vpnType) &&
              <Button large rounded outline text="Re-install" onClick={onInstallClick}/>
          }
          { (!isDeviceAssignedRecently && vpnAssignedDeviceId && currentAccountKiboshVpnDevice.vpnType && account?.accountId == 10010) &&
              <Button large rounded outline text="Download OpenVPN Config File" onClick={onOpenVpnConfigClick}/>
          }
          { (vpnAssignedDeviceId && currentAccountKiboshVpnDevice.vpnType) &&
              <Button large rounded outline text="Unassign" onClick={onDeleteClick}/>
          }
          { (vpnAssignedDeviceId && !currentAccountKiboshVpnDevice.vpnType) &&
              <Button large rounded outline text="Clear" onClick={onDeleteClick}/>
          }
        </group>
      }
      </view>
    </Popup>
  );
}
